import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { BookOpenCheck, ArrowRight, Plus, X, History, BarChart3 } from "lucide-react";
import api from "@/lib/api";
import { Header } from "@/pages/CollegePredictorPage";
import { PatternBadge, err, trackEvent } from "./shared";

export default function PrepHome() {
  const navigate = useNavigate();
  const [cat, setCat] = useState(null);
  const [targets, setTargets] = useState([]);
  const [level, setLevel] = useState("ug");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/test-prep/catalogue").then((r) => setCat(r.data)).catch((e) => toast.error(err(e)));
    api.get("/test-prep/targets").then((r) => setTargets(r.data.targets)).catch(() => {});
    trackEvent("test_preparation_viewed");
  }, []);

  const choose = async (exam) => {
    setBusy(exam.id);
    try {
      if (!targets.some((t) => t.id === exam.id)) {
        const { data } = await api.post("/test-prep/targets", { exam_id: exam.id });
        setTargets(data.targets);
      }
      navigate(`/prep/exam/${exam.id}`);
    } catch (e) { toast.error(err(e)); } finally { setBusy(null); }
  };

  const remove = async (e, examId) => {
    e.preventDefault(); e.stopPropagation();
    try { const { data } = await api.delete(`/test-prep/targets/${examId}`); setTargets(data.targets); } catch (x) { toast.error(err(x)); }
  };

  if (!cat) return <div className="p-8 text-slate-400">Loading...</div>;
  const exams = cat.exams.filter((e) => e.level === level);

  return (
    <div data-testid="prep-home" className="mx-auto max-w-6xl px-4 sm:px-8 py-8">
      <Header icon={BookOpenCheck} eyebrow="Test Preparation" title="Prepare. Practice. Test. Improve."
        desc="Prepare, practice, test and improve with EntranceZone AI — then carry your performance into Rank & College Prediction." />

      {targets.length > 0 && (
        <section className="mb-10">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold md:text-lg">My Preparation</h2>
            <div className="flex gap-3 text-xs">
              <Link to="/prep/history" data-testid="prep-history-link" className="inline-flex items-center gap-1 text-slate-400 hover:text-cyan-300"><History className="h-3.5 w-3.5" /> Test History</Link>
              <Link to="/prep/performance" data-testid="prep-performance-link" className="inline-flex items-center gap-1 text-slate-400 hover:text-cyan-300"><BarChart3 className="h-3.5 w-3.5" /> My Performance</Link>
            </div>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {targets.map((t) => (
              <Link key={t.id} to={`/prep/exam/${t.id}`} data-testid={`target-card-${t.slug}`}
                className="group relative rounded-2xl border border-cyan-400/25 bg-gradient-to-br from-[#0F2A33] to-[#131C2E] p-5 transition-transform hover:scale-[1.01]">
                <button onClick={(e) => remove(e, t.id)} data-testid={`target-remove-${t.slug}`} title="Remove target"
                  className="absolute right-3 top-3 rounded-full p-1 text-slate-500 hover:bg-white/10 hover:text-white"><X className="h-3.5 w-3.5" /></button>
                <p className="font-mono-data text-[11px] uppercase tracking-wider text-cyan-400">{t.level === "class12" ? "Class 12" : `${t.level.toUpperCase()} Entrance`} · {t.session}</p>
                <p className="mt-1 text-lg font-bold">{t.name} {t.level !== "class12" && t.session}</p>
                <div className="mt-2"><PatternBadge status={t.pattern_status} /></div>
                <p className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-cyan-300">Open preparation <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></p>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section>
        <h2 className="text-base font-bold md:text-lg">What are you preparing for?</h2>
        <p className="mt-1 text-sm text-slate-400">Choose a stage, then pick your exam. You can add more than one target.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {cat.levels.map((l) => (
            <button key={l.id} data-testid={`level-tab-${l.id}`} onClick={() => setLevel(l.id)}
              className={`rounded-full px-4 py-2 text-xs font-semibold transition-all ${level === l.id ? "bg-cyan-400 text-[#041016]" : "bg-white/5 text-slate-400 hover:text-white"}`}>{l.label}</button>
          ))}
        </div>
        <p className="mt-2 text-xs text-slate-500">{cat.levels.find((l) => l.id === level)?.desc}</p>

        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {exams.map((e, i) => {
            const selected = targets.some((t) => t.id === e.id);
            return (
              <motion.button key={e.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
                data-testid={`exam-card-${e.slug}`} onClick={() => choose(e)} disabled={busy === e.id}
                className={`rounded-2xl border p-4 text-left transition-all hover:border-cyan-400/40 ${selected ? "border-cyan-400/30 bg-cyan-400/5" : "border-white/8 bg-[#131C2E]"}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-semibold">{e.name} {e.level !== "class12" && <span className="text-slate-400">{e.session}</span>}</p>
                    <p className="mt-0.5 text-xs text-slate-500">{e.question_count} practice questions{e.pattern ? ` · ${e.pattern.questions} Q / ${e.pattern.minutes} min mock` : ""}</p>
                  </div>
                  <span className={`rounded-full p-1.5 ${selected ? "bg-cyan-400 text-[#041016]" : "bg-white/5 text-slate-400"}`}>{selected ? <ArrowRight className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}</span>
                </div>
                <div className="mt-3"><PatternBadge status={e.pattern_status} /></div>
              </motion.button>
            );
          })}
        </div>
      </section>
    </div>
  );
}
